<script setup>
// The real Book page, running on demo data, inside /features/bookings: the
// same BookingPageBody the club page draws (the All tab over two sets of
// booking rules, the week strip, the allowance chips, the day grid with its
// legend) and the same BookingSheet, fed a quote and a booking by
// utils/bookingDemo instead of the API. A booking made here lands on the grid
// as "You", which is what makes it a demo rather than a screenshot. A switch
// shows the page as a member and as the club's admin (no limits, any length).
import {computed, onMounted, ref} from 'vue';
import BookingPageBody from '~/components/bookings/BookingPageBody.vue';
import BookingSheet from '~/components/bookings/BookingSheet.vue';
import {areaWords} from '~/utils/areaWords';
import {
  BOOKING_DEMO_CLUB, bookingDemoAdminViewer, bookingDemoAvailability, bookingDemoBook, bookingDemoDate, bookingDemoQuote, bookingDemoViewer,
} from '~/utils/bookingDemo';
import {memberLegend} from '~/utils/bookingLegend';
import {buildGrid} from '~/utils/bookingsMember';
import {
  allowanceChips, dayGrid, dayStrip, groupAllowance, groupTabs, jumpWindow, legendKeys, pickTab, shownDate, weekLabel, weekNav,
} from '~/utils/bookingWeek';

const props = defineProps({
  // show the member / admin switch over the page
  controls: {type: Boolean, default: true},
});

const {setAlertMessage} = useAlertService();

const asAdmin = ref(false);
const viewer = computed(() => (asAdmin.value ? bookingDemoAdminViewer : bookingDemoViewer));
// The week's days are the one thing a booking changes, so they live in state; everything else derives.
const days = ref(bookingDemoAvailability().dayList);
const activeBookings = ref(1);
const availability = computed(() => {
  const v = {...viewer.value, activeBookings: asAdmin.value ? 0 : activeBookings.value};
  v.byGroup = Object.fromEntries(Object.entries(v.byGroup).map(([k, a]) => [k, {...a, activeBookings: v.activeBookings}]));
  return {...bookingDemoAvailability({viewer: v}), dayList: days.value};
});
const nowIso = ref(new Date().toISOString());
const todayDate = computed(() => bookingDemoDate(0));
const daysAhead = computed(() => viewer.value.daysAhead ?? 7);

// ── Tabs, the week and the day (the page's query, here plain state) ──
const groupId = ref(null);
const sportId = ref(null);
const wantedDate = ref(null);
const tabs = computed(() => groupTabs(availability.value, sportId.value, {all: true}));
const tab = computed(() => pickTab(tabs.value, groupId.value));
const shown = computed(() => shownDate(wantedDate.value, todayDate.value, daysAhead.value));
const monday = computed(() => shown.value.monday);

const allowanceFor = (t) => (!t || t.all ? null : groupAllowance(availability.value.viewer, t.id));
const priceFor = (a) => a?.priceMinor ?? availability.value.viewer?.priceMinor ?? null;
const allowance = computed(() => allowanceFor(tab.value));
const ctxFor = (t) => {
  const a = allowanceFor(t);
  return {viewer: availability.value.viewer, allowance: a, canTap: true, tapReason: '', priceMinor: priceFor(a), currency: 'gbp', now: nowIso.value, noun: t?.noun, paymentsRefusal: null};
};
const cellCtx = computed(() => ({...ctxFor(tab.value), forTab: ctxFor}));
const grid = computed(() => dayGrid(days.value, shown.value.date, tab.value, cellCtx.value));
const strip = computed(() => dayStrip(monday.value, days.value, {todayDate: todayDate.value, selectedDate: shown.value.date, tab: tab.value, ...cellCtx.value}));
const nav = computed(() => weekNav(monday.value, todayDate.value, daysAhead.value));
const label = computed(() => weekLabel(monday.value));
const jump = computed(() => jumpWindow(todayDate.value, daysAhead.value));
const noun = computed(() => areaWords(availability.value).noun);
const pastOpen = ref(false);
const foldable = computed(() => (grid.value?.startedRows ?? 0) > 0 && (grid.value?.startedRows ?? 0) < (grid.value?.rows?.length ?? 0));
const legendItems = computed(() => {
  const keys = legendKeys(grid.value?.columns ?? [], {pastShown: pastOpen.value || !foldable.value});
  return memberLegend(tab.value?.noun || noun.value).filter((l) => keys.includes(l.key));
});
const chips = computed(() => allowanceChips(availability.value.viewer, allowance.value, tab.value?.cancelCutoffMinutes ?? null));

const pickWeek = (to) => { wantedDate.value = to || null; };
const pickDay = (date) => { wantedDate.value = date; };
const pickGroup = (id) => { groupId.value = id; };
const pickSport = (id) => { sportId.value = id; };
const onBlocked = (hint) => { if (hint) setAlertMessage({message: hint, color: 'info'}); };
const touch = ref(false);
onMounted(() => { touch.value = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches; });

// ── Tapping a cell: the real sheet, on the demo's quote ──
const pick = ref(null);
const sheetOpen = ref(false);
const onTap = ({cell, column, slots}) => {
  const day = days.value.find((d) => d.date === cell.date);
  const area = column?.area ?? availability.value.areas.find((a) => a.id === cell.areaId);
  const group = availability.value.groups.find((g) => g.id === (area?.groupId ?? tab.value?.id)) ?? null;
  if (!day || !area) return;
  const col = buildGrid(day, {groups: availability.value.groups, areas: availability.value.areas, sportId: sportId.value}).columns.find((c) => c.area.id === area.id);
  const index = col?.cells.findIndex((c) => c.slot.startsAt === cell.startsAt) ?? -1;
  if (!col || index < 0) return;
  pick.value = {area, slot: col.cells[index].slot, cell: col.cells[index].cell, group, column: col, index, date: cell.date, slots: Math.max(1, slots ?? 1)};
  sheetOpen.value = true;
};
const onExplain = ({cell, reason}) => {
  if (reason) setAlertMessage({message: reason, color: 'error'});
  else if (touch.value && cell?.tip) setAlertMessage({message: cell.tip, color: 'info'});
};
const selected = computed(() => (sheetOpen.value && pick.value ? {areaId: pick.value.area.id, startsAt: pick.value.slot.startsAt, slots: pick.value.slots ?? 1} : null));
const sheetNoun = computed(() => {
  const gid = pick.value?.area?.groupId;
  const own = gid && tab.value?.all ? tab.value.tabs.find((t) => t.id === gid) : null;
  return own?.noun || tab.value?.noun || noun.value;
});

// The demo's API: a quote from the area's own rules, then the booking lands on the grid as "You".
const demo = computed(() => ({
  quote: (body) => bookingDemoQuote(body, {viewer: availability.value.viewer}),
  book: (body) => {
    const res = bookingDemoBook(body);
    const p = pick.value;
    if (p) {
      const slotMinutes = p.group?.slotMinutes ?? 60;
      const start = new Date(p.slot.startsAt).getTime();
      const end = start + Math.max(1, body.slots ?? 1) * slotMinutes * 60_000;
      days.value = days.value.map((d) => (d.date !== p.date ? d : {
        ...d,
        groups: d.groups.map((g) => (g.groupId !== p.group?.id ? g : {
          ...g,
          slots: g.slots.map((s) => {
            const t = new Date(s.startsAt).getTime();
            if (t < start || t >= end) return s;
            return {...s, areas: s.areas.map((a) => (a.areaId === p.area.id ? {areaId: a.areaId, status: 'mine', mine: true, bookingId: 'b-demo-' + t} : a))};
          }),
        })),
      }));
      activeBookings.value += 1;
    }
    return res;
  },
}));
const sheetClub = {id: BOOKING_DEMO_CLUB.id, name: BOOKING_DEMO_CLUB.name, players: BOOKING_DEMO_CLUB.players};

const bodyProps = computed(() => ({
  availability: availability.value, tabs: tabs.value, tab: tab.value, grid: grid.value, days: strip.value,
  weekLabel: label.value, nav: nav.value, jumpMin: jump.value.min, jumpMax: jump.value.max, chips: chips.value, legendItems: legendItems.value,
  sports: availability.value.sports, sportId: sportId.value, allowance: allowance.value, priceMinor: priceFor(allowance.value), currency: 'gbp',
  gridLoading: false, footLine: null, reasonLine: null, failed: null, notice: shown.value.notice ?? null,
  signedIn: true, visitorsAllowed: true, visitorsReason: null, signInTo: null, selected: selected.value, pastOpen: pastOpen.value, touch: touch.value,
}));
const bodyEvents = {pickGroup, pickDay, pickWeek, pickDate: pickDay, pickSport, tap: onTap, explain: onExplain, blocked: onBlocked, 'update:pastOpen': (v) => { pastOpen.value = v; }};
</script>

<template>
  <div class="bds">
    <div v-if="controls" class="bds-controls">
      <div class="tl-seg" role="group" aria-label="Who is looking">
        <button type="button" class="tl-seg__opt" :class="{'tl-seg__opt--active': !asAdmin}" @click="asAdmin = false">As a member</button>
        <button type="button" class="tl-seg__opt" :class="{'tl-seg__opt--active': asAdmin}" @click="asAdmin = true">As the club admin</button>
      </div>
      <span class="bds-hint">{{ asAdmin ? 'An admin books any length, as far ahead as they like, with a note where a member rule is passed.' : 'A Senior member: up to 3 h at a time, 14 days ahead, four bookings at once, £4.00 a court slot. Tap a free slot, or drag down for longer.' }}</span>
    </div>
    <div class="bds-page">
      <client-only>
        <booking-page-body v-bind="bodyProps" v-on="bodyEvents"/>
        <booking-sheet v-model="sheetOpen" :pick="pick" :initial-slots="pick?.slots ?? 1" :noun="sheetNoun" :club="sheetClub" :viewer="availability.viewer"
                       :date="pick?.date ?? todayDate" :today-date="todayDate" :demo="demo"/>
        <template #fallback>
          <div class="bds-skeleton" aria-hidden="true"/>
        </template>
      </client-only>
    </div>
  </div>
</template>

<style scoped lang="scss">
.bds-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 16px;
  margin-bottom: 14px;
}

.bds-hint {
  font-size: 12.5px;
  line-height: 1.5;
  color: rgb(var(--v-theme-textSubtle));
}

.bds-page {
  text-align: left;
}

.bds-skeleton {
  height: 520px;
  border-radius: 16px;
  background: rgb(var(--v-theme-light));
}
</style>
